/*
 * Copyright (c) 2024-2025 Nodals AI Ltd
 * All rights reserved.
 */

import { EventDispatcher, EventObject } from '../events';
import Library from '../library';
import { Logger } from '../logger';
import { GlobalFactStore } from '../rules/facts/store';

import type { Facts } from '../rules';
import type { KeyValue } from '../types';
import type { UserConsent } from '../types/prebid';
import { debugPixel } from '../util';
import type { ReportingEvents } from './events';

export interface AnalyticsConfig {
  enabled?: boolean;
  recordBids?: boolean;
  endpoint?: {
    origin: string;
  };
}

export interface AnalyticsOptions {
  config: AnalyticsConfig;
  propertyId: string;
}

const enum RequestType {
  GET = 'GET',
  POST = 'POST',
}

const BASE_PATH = '/d/v1';
const OPPORTUNITY_ENDPOINT = '/opp';
const IMPRESSION_ENDPOINT = '/imp';
const AD_INTERACTION_ENDPOINT = '/int';
const VIEWABLE_IMP_ENDPOINT = '/vi';
const FILTERED_SELECTION_REPORT_ENDPOINT = '/ckv';
const PAGE_VIEW_ENDPOINT = '/pv';
const logger = Logger.getInstance();

enum EventType {
  IMPRESSION = 'impression',
  OPPORTUNITY = 'opportunity',
  AD_INTERACTION = 'interaction',
  VIEWABLE_IMPRESSION = 'viewableImpression',
  FILTERED_SELECTION_REPORT = 'filteredSelectionReport',
  PAGE_VIEW = 'pageView',
}

class AnalyticsRecorder {
  private config: AnalyticsConfig;
  private enabled: boolean = true;
  private propertyId: string;
  private auctionNumber: number = 0;
  private eventDispatcher: EventDispatcher<ReportingEvents>;

  constructor(eventDispatcher: EventDispatcher<ReportingEvents>, options: AnalyticsOptions) {
    this.eventDispatcher = eventDispatcher;
    this.config = options.config;
    this.propertyId = options.propertyId;

    if (this.config.enabled !== undefined) {
      this.enabled = Boolean(this.config.enabled);
    }

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    this.eventDispatcher.on('AdImpression', (event) => this.handleAdImpression(event));
    this.eventDispatcher.on('AdViewableImpression', (event) =>
      this.handleAdViewableImpression(event)
    );
    this.eventDispatcher.on('AdInteraction', (event) => this.handleAdInteraction(event));
    this.eventDispatcher.on('AuctionEnd', (event) => this.handleAuctionEnd(event));
    this.eventDispatcher.on('TargetingSelectionComplete', (event) =>
      this.handleTargetingSelectionComplete(event)
    );
    this.eventDispatcher.on('PageView', (event) => this.handlePageView(event));
  }

  private getEndpointUrl(eventType: EventType): string {
    const origin: string = this.config!.endpoint!.origin;
    const baseUrl = `${origin}${BASE_PATH}`;
    switch (eventType) {
      case EventType.OPPORTUNITY:
        return `${baseUrl}${OPPORTUNITY_ENDPOINT}`;
      case EventType.IMPRESSION:
        return `${baseUrl}${IMPRESSION_ENDPOINT}`;
      case EventType.AD_INTERACTION:
        return `${baseUrl}${AD_INTERACTION_ENDPOINT}`;
      case EventType.VIEWABLE_IMPRESSION:
        return `${baseUrl}${VIEWABLE_IMP_ENDPOINT}`;
      case EventType.FILTERED_SELECTION_REPORT:
        return `${baseUrl}${FILTERED_SELECTION_REPORT_ENDPOINT}`;
      case EventType.PAGE_VIEW:
        return `${baseUrl}${PAGE_VIEW_ENDPOINT}`;
    }
  }

  private getInitialQueryParamsMap(
    userConsent: UserConsent,
    requestType: RequestType
  ): Record<string, string> {
    let params: Record<string, string> = {
      ...this.generateConsentParamsMap(userConsent),
      rnd: String(Math.random()),
    };
    if (requestType === RequestType.GET) {
      params.dl = `${Library.name}:${Library.version}`;
    }
    return params;
  }

  private handleAdImpression(event: EventObject<ReportingEvents['AdImpression']>): void {
    const { userConsent, ...data } = event.data;

    if (!this.enabled) {
      logger.info('Impression recording is disabled');
      return;
    }

    logger.info(
      `Logging impression for opportunity: ${data.opportunityId} ;impression: ${data.id}`
    );
    const endpointUrl: string = this.getEndpointUrl(EventType.IMPRESSION);
    const parameterMap: Record<string, string> = this.getInitialQueryParamsMap(
      userConsent,
      RequestType.GET
    );
    Object.assign(parameterMap, {
      id: data.id,
      rid: data.opportunityId,
      did: data.deliveryId,
      eai: data.advertiserId,
      eadms: data.advertiserDomains,
      gad: data.adUnit,
      lid: data.lineItemId,
      crw: data.renderedData?.size?.width,
      crh: data.renderedData?.size?.height,
      crid: data.creativeId,
      dlid: data.nodalsDealId,
      pvid: data.pvid,
      sid: data.sid,
      uid: data.uid,
    });

    if (data.kvs && data.kvs.length > 0) {
      parameterMap.kvs = this.serializeKeyValues(data.kvs);
    }

    const querystring: string = new URLSearchParams(
      Object.entries(parameterMap).filter(([_, value]) => value !== null && value !== undefined)
    ).toString();

    const debugPayload: Record<string, any> = {
      en: data.lineItemId ? 'directImpressionLogged' : 'programmaticImpressionLogged',
      adUnitCode: data.adUnit,
    };
    if (data.lineItemId) {
      debugPayload.advertiserId = data.advertiserId;
    }
    debugPixel(debugPayload);
    this.makeGetRequest(`${endpointUrl}?${querystring}`);
  }

  private handleAdViewableImpression(
    event: EventObject<ReportingEvents['AdViewableImpression']>
  ): void {
    const { userConsent, ...data } = event.data;

    if (!this.enabled) {
      logger.info('Event recording is disabled');
      return;
    }

    logger.info(
      `Logging viewable impression for opportunity: ${data.opportunityId} ;impression: ${data.impressionId}`
    );
    const endpointUrl: string = this.getEndpointUrl(EventType.VIEWABLE_IMPRESSION);
    const parameterMap: Record<string, string> = this.getInitialQueryParamsMap(
      userConsent,
      RequestType.GET
    );
    Object.assign(parameterMap, {
      id: data.impressionId,
      rid: data.opportunityId,
      did: data.deliveryId,
      eai: data.advertiserId,
      eadms: data.advertiserDomains,
      gad: data.adUnit,
      lid: data.lineItemId,
      dlid: data.nodalsDealId,
      pvid: data.pvid,
      sid: data.sid,
      uid: data.uid,
    });

    const querystring: string = new URLSearchParams(
      Object.entries(parameterMap).filter(([_, value]) => value != null)
    ).toString();
    this.makeGetRequest(`${endpointUrl}?${querystring}`);
  }

  private handleAdInteraction(event: EventObject<ReportingEvents['AdInteraction']>): void {
    const { userConsent, ...data } = event.data;

    if (!this.enabled) {
      logger.info('Event recording is disabled');
      return;
    }

    logger.info(
      `Logging interaction for opportunity: ${data.opportunityId} ;impression: ${data.impressionId}`
    );
    const endpointUrl: string = this.getEndpointUrl(EventType.AD_INTERACTION);
    const parameterMap: Record<string, string> = this.getInitialQueryParamsMap(
      userConsent,
      RequestType.GET
    );
    Object.assign(parameterMap, {
      id: data.impressionId,
      rid: data.opportunityId,
      did: data.deliveryId,
      eai: data.advertiserId,
      eadms: data.advertiserDomains,
      gad: data.adUnit,
      lid: data.lineItemId,
      dlid: data.nodalsDealId,
      pvid: data.pvid,
      sid: data.sid,
      uid: data.uid,
    });

    const querystring: string = new URLSearchParams(
      Object.entries(parameterMap).filter(([_, value]) => value != null)
    ).toString();
    this.makeGetRequest(`${endpointUrl}?${querystring}`);
  }

  private handleAuctionEnd(event: EventObject<ReportingEvents['AuctionEnd']>): void {
    const { opportunityId, placements, bids, userConsent, pvid, sid, uid } = event.data;

    if (!this.enabled) {
      logger.info('Analytics recording is disabled');
      return;
    }

    // Record opportunity with bid data
    logger.info(`Logging opportunity for id: ${opportunityId}`);
    this.auctionNumber++;
    const opportunityEndpointUrl: string = this.getEndpointUrl(EventType.OPPORTUNITY);
    const parameterMap: Record<string, string> = this.getInitialQueryParamsMap(
      userConsent,
      RequestType.POST
    );
    const opportunityPayload: Record<string, any> = this.generateOpportunityPayload(pvid, sid, uid);
    opportunityPayload.id = opportunityId;

    for (const placement of placements) {
      const placementData: any = {
        gad: placement.adUnitCode,
        v: placement.visibility,
        ss: placement.supportedSizes
          .filter((obj: any) => obj != null)
          .map((obj: any) => ({
            w: obj.width,
            h: obj.height,
          })),
      };

      if (this.config.recordBids === true) {
        placementData.bds = bids[placement.adUnitCode]?.map((bid: any) => ({
          ptr: bid.bidder,
          advd: bid?.advertiserDomains,
          bdp: bid.cpm,
          mtpe: bid.mediaType,
          dlid: bid?.dealId,
        }));
      }

      opportunityPayload.plcmts.push(placementData);
    }

    const querystring: string = new URLSearchParams(parameterMap).toString();
    this.makePostRequest(`${opportunityEndpointUrl}?${querystring}`, opportunityPayload);
  }

  private handleTargetingSelectionComplete(
    event: EventObject<ReportingEvents['TargetingSelectionComplete']>
  ): void {
    const { opportunityId, selectionData, userConsent } = event.data;

    if (!this.enabled) {
      logger.info('Analytics recording is disabled');
      return;
    }

    // Record filtered selection data
    logger.info(`Logging filtered selection reporting for opportunity: ${opportunityId}`);
    const selectionEndpointUrl: string = this.getEndpointUrl(EventType.FILTERED_SELECTION_REPORT);
    const parameterMap: Record<string, string> = this.getInitialQueryParamsMap(
      userConsent,
      RequestType.POST
    );
    const selectionPayload: Record<string, any> = {
      id: opportunityId,
      plcmts: selectionData,
      dl: `${Library.name}:${Library.version}`,
    };

    const querystring: string = new URLSearchParams(parameterMap).toString();
    this.makePostRequest(`${selectionEndpointUrl}?${querystring}`, selectionPayload);
  }

  private handlePageView(event: EventObject<ReportingEvents['PageView']>): void {
    const { id, sid, uid, userConsent } = event.data;

    if (!this.enabled) {
      logger.info('Analytics recording is disabled');
      return;
    }

    // Record page view data
    logger.info(`Logging page view: ${id}`);
    const pageViewEndpointUrl: string = this.getEndpointUrl(EventType.PAGE_VIEW);
    const parameterMap: Record<string, string> = this.getInitialQueryParamsMap(
      userConsent,
      RequestType.POST
    );
    const pageViewPayload: Record<string, any> = this.generatePageViewPayload(id, sid, uid);

    const querystring: string = new URLSearchParams(parameterMap).toString();
    this.makePostRequest(`${pageViewEndpointUrl}?${querystring}`, pageViewPayload);
  }

  private serializeKeyValues(keyValues: KeyValue[]): string {
    return keyValues.map((kv) => `${kv.key}=${kv.value}`).join(':');
  }

  private generateConsentParamsMap(userConsent: UserConsent): Record<string, string> {
    return {
      gdpr_consent: userConsent?.gdpr?.consentString ?? '',
      gdpr: userConsent?.gdpr?.gdprApplies ? '1' : '0',
      us_privacy: userConsent?.uspConsent ?? '',
      gpp: userConsent?.gpp?.gppString ?? '',
      gpp_sid:
        userConsent.gpp && Array.isArray(userConsent.gpp.applicableSections)
          ? userConsent.gpp.applicableSections.join(',')
          : '',
    };
  }

  private makePostRequest(url: string, payload: any): void {
    logger.info(`Making HTTP POST analytics request to ${url}`);
    navigator.sendBeacon(url, JSON.stringify(payload));
  }

  private makeGetRequest(url: string, payload: any = undefined): void {
    let urlWithOptPayload: string = url;
    if (payload) {
      let payloadBase64: string;
      try {
        const encoder: TextEncoder = new TextEncoder();
        const payloadBytes: Uint8Array = encoder.encode(JSON.stringify(payload));
        payloadBase64 = btoa(String.fromCharCode(...payloadBytes));
      } catch (error) {
        payloadBase64 = btoa(JSON.stringify(payload));
      }
      urlWithOptPayload = `${url}&data=${payloadBase64}`;
    }
    logger.info(`Making HTTP GET analytics request to ${urlWithOptPayload}`);
    new Image().src = urlWithOptPayload;
  }

  private generateOpportunityPayload(pvid: string, sid: string, uid?: string): Record<string, any> {
    const globalFacts: Facts = GlobalFactStore.getInstance().getFacts();
    const payload: any = {
      num: this.auctionNumber,
      pid: this.propertyId,
      pvid: pvid,
      sid: sid,
      ref: globalFacts['page.url'] ?? '',
      br: globalFacts['browser.name'] ?? '',
      bl: globalFacts['browser.lang'] ?? '',
      bw: globalFacts['browser.width'] ?? 0,
      bh: globalFacts['browser.height'] ?? 0,
      dt: globalFacts['device.type'] ?? '',
      os: globalFacts['device.os'] ?? '',
      to: globalFacts['time.offset'],
      td: globalFacts['time.day'],
      th: globalFacts['time.hour'],
      tm: globalFacts['time.minutes'],
      cy: globalFacts['geo.city'] ?? '',
      ct: globalFacts['geo.country'] ?? '',
      pc: globalFacts['geo.postcode'] ?? '',
      dl: `${Library.name}:${Library.version}`,
      plcmts: [],
    };
    const publisherCohorts: String[] = globalFacts['publisher.cohort.ids'];
    if (publisherCohorts && publisherCohorts.length > 0) {
      payload.pcids = publisherCohorts;
    }
    if (uid) {
      payload.uid = uid;
    }
    return payload;
  }

  private generatePageViewPayload(id: string, sid: string, uid?: string): Record<string, any> {
    const globalFacts: Facts = GlobalFactStore.getInstance().getFacts();
    const payload: any = {
      id: id,
      sid: sid,
      pid: this.propertyId,
      ref: globalFacts['page.url'] ?? '',
      br: globalFacts['browser.name'] ?? '',
      bl: globalFacts['browser.lang'] ?? '',
      bw: globalFacts['browser.width'] ?? 0,
      bh: globalFacts['browser.height'] ?? 0,
      dt: globalFacts['device.type'] ?? '',
      os: globalFacts['device.os'] ?? '',
      to: globalFacts['time.offset'],
      cy: globalFacts['geo.city'] ?? '',
      ct: globalFacts['geo.country'] ?? '',
      pc: globalFacts['geo.postcode'] ?? '',
      dl: `${Library.name}:${Library.version}`,
    };

    // Add uid only if provided
    if (uid) {
      payload.uid = uid;
    }

    return payload;
  }
}

export type { ReportingEvents } from './events';
export { AnalyticsRecorder };
